import { AppInstance } from "./instance";

export const fetchSettingsFromAPI = async () => {
  try {
    const response = await AppInstance.get("/settings");

    if (response.data?.success) {
      const settingsArray = response.data.data || [];

      // Validate that we have an array of settings
      if (!Array.isArray(settingsArray)) {
        console.warn(
          "Settings API returned invalid data structure - expected array"
        );
        return {
          success: false,
          error: "Invalid settings data structure - expected array",
        };
      }

      // Convert array to key-value object for easier access
      const settings = {};
      settingsArray.forEach((setting) => {
        if (setting.key && setting.value !== undefined) {
          // Try to parse JSON values, fallback to string
          try {
            settings[setting.key] = JSON.parse(setting.value);
          } catch {
            settings[setting.key] = setting.value;
          }
        }
      });

      // Save to localStorage
      localStorage.setItem("siteSettings", JSON.stringify(settings));

      return {
        success: true,
        data: settings,
      };
    } else {
      console.warn(
        "Settings API returned unsuccessful response:",
        response.data
      );
      return {
        success: false,
        error: response.data?.message || "Failed to fetch settings",
      };
    }
  } catch (error) {
    console.error("Error fetching settings:", error);

    // Handle specific error types
    if (error.response) {
      // Server responded with error status
      if (error.response.status === 404) {
        return {
          success: false,
          error: "Settings endpoint not found",
        };
      } else if (error.response.status >= 500) {
        return {
          success: false,
          error: "Server error occurred while fetching settings",
        };
      } else {
        return {
          success: false,
          error:
            error.response.data?.message ||
            `HTTP ${error.response.status} error`,
        };
      }
    } else if (error.request) {
      // Network error
      return {
        success: false,
        error: "Network error - unable to connect to server",
      };
    } else {
      // Other error
      return {
        success: false,
        error: error.message || "Unknown error occurred",
      };
    }
  }
};

export const getSettingsFromStorage = () => {
  try {
    const storedSettings = localStorage.getItem("siteSettings");
    if (storedSettings) {
      const settings = JSON.parse(storedSettings);

      // Validate stored settings
      if (settings && typeof settings === "object") {
        return settings;
      } else {
        console.warn("Invalid settings found in localStorage, removing...");
        localStorage.removeItem("siteSettings");
        return null;
      }
    }
    return null;
  } catch (error) {
    console.error("Error loading settings from storage:", error);
    // Remove corrupted data
    localStorage.removeItem("siteSettings");
    return null;
  }
};

export const getSettingValue = (settings, key, defaultValue = "") => {
  if (!settings || typeof settings !== "object") return defaultValue;

  // Handle nested keys like 'site.logo'
  const keys = key.split(".");
  let value = settings;

  for (const k of keys) {
    if (value && typeof value === "object" && k in value) {
      value = value[k];
    } else {
      return defaultValue;
    }
  }

  return value || defaultValue;
};

// Common settings getters based on actual API response
export const getSiteLogo = (settings) => {
  return getSettingValue(settings, "site_logo") || "/assets/img/logo/logo.png";
};

export const getSiteFavIcon = (settings) => {
  return getSettingValue(settings, "site_fav_icon") || null;
};

export const getSiteLogoLight = (settings) => {
  return getSettingValue(settings, "site_logo_light") || "/assets/img/logo/logo.png";
};

export const getSiteLogoDark = (settings) => {
  return getSettingValue(settings, "site_logo_dark") || "/assets/img/logo/logo.png";
};

export const getSiteName = (settings) => {
  return getSettingValue(settings, "company_name") || process.env.REACT_APP_SITE_NAME || "E-Commerce Store";
};

export const getSiteTitle = (settings) => {
  return getSettingValue(settings, "site_title") || process.env.REACT_APP_SITE_NAME || "E-Commerce Store";
};

export const getSiteDescription = (settings) => {
  return getSettingValue(settings, "site_description") || "";
};

export const getCompanyAddress = (settings) => {
  const addressData = getSettingValue(settings, "company_address");
  if (typeof addressData === "object" && addressData !== null) {
    return addressData;
  }
  return null;
};

export const getCompanyPhone = (settings) => {
  return getSettingValue(settings, "company_phone_number") || "";
};

export const getCompanyEmail = (settings) => {
  const addressData = getCompanyAddress(settings);
  return addressData?.email || "";
};

export const getCurrencySymbol = (settings) => {
  return getSettingValue(settings, "currency_symbol") || "AED";
};

export const getCurrencyCode = (settings) => {
  return getSettingValue(settings, "currency_code") || "AED";
};

export const getOrderIdPrefix = (settings) => {
  return getSettingValue(settings, "order_id_prefix") || "ORD";
};

export const getProductCodePrefix = (settings) => {
  return getSettingValue(settings, "product_code_prefix") || "PROD";
};

export const getCustomerPrefix = (settings) => {
  return getSettingValue(settings, "customer_prefix") || "CS-";
};

export const getFixedDeliveryCharge = (settings) => {
  return (
    parseFloat(getSettingValue(settings, "fixed_delivery_charge", "0")) || 0
  );
};

export const getIsFreeDelivery = (settings) => {
  return getSettingValue(settings, "is_free_delivery") === "1";
};

export const getMinimumItemPrice = (settings) => {
  return parseFloat(getSettingValue(settings, "minimum_item_price", "1")) || 1;
};

export const getFilesBaseUrl = (settings) => {
  return getSettingValue(settings, "files_base_url") || "";
};

export const getCompanyVatRegistration = (settings) => {
  return getSettingValue(settings, "company_vat_registration") || "";
};

// Clear settings from storage (useful for testing or reset)
export const clearSettingsFromStorage = () => {
  try {
    localStorage.removeItem("siteSettings");
    return true;
  } catch (error) {
    console.error("Error clearing settings from storage:", error);
    return false;
  }
};
